import React, { useEffect, useState } from "react";
import { format } from "date-fns";
import Tippy from "@tippyjs/react";
import "tippy.js/dist/tippy.css";

import Container from "react-bootstrap/Container";
import Col from "react-bootstrap/Col";
import Row from "react-bootstrap/Row";
import Table from "react-bootstrap/Table";
import { Button } from "react-bootstrap";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import "./ClassOverview.css";
import "./MyColleaguesOverview.css";

import InviteColleaguesButton from "./InviteColleaguesButton";
import SimpleNavigatioButton from "./SimpleNavigationButton";
import AlpacaLeadChangeModal from "./AlpacaLeadChangeModal";
import SchoolRollNumberInput from "./SchoolRollNumberInput";

import {
  getPendingTeacherBySchoolId,
  resendTeacherInvite,
  addAlpacaLeadRole,
  removeAlpacaLeadRole,
  deletePendingTeacher, // Add this import
} from "../services/APIClient";

function UserExistenceOverview(props) {
  const [loading, setLoading] = useState(true);
  const [combinedTeachers, setCombinedTeachers] = useState([]);
  const [feedback, setFeedback] = useState("");
  const [isError, setIsError] = useState(false);
  const [currentAlpacaLead, setCurrentAlpacaLead] = useState(null);
  const [selectedTeacher, setSelectedTeacher] = useState("");
  const [schoolid, setSchoolid] = useState(props.schoolid || null);

  const notifySuccess = (msg) => toast.success(msg);
  const notifyError = (msg) => toast.error(msg);

  function formatDate(dateString) {
    if (!dateString) return "N/A";
    return format(new Date(dateString), "MMMM d, yyyy h:mm aa");
  }

  const canResendInvite = (lastInviteSent) => {
    const now = new Date();
    const lastSent = new Date(lastInviteSent);
    return !lastInviteSent || now.getTime() - lastSent.getTime() > 600000;
  };

  const handleResendInvite = async (teacher) => {
    const now = new Date();
    if (!canResendInvite(teacher.lastinvitesent)) {
      toast.info("Please wait before resending the invite.");
      return;
    }

    try {
      await resendTeacherInvite(teacher.id);
      setCombinedTeachers((prevTeachers) =>
        prevTeachers?.map((t) =>
          t.id === teacher.id ? { ...t, lastinvitesent: now.toISOString() } : t
        )
      );
      notifySuccess(`Invite resent successfully to ${teacher.name}!`);
    } catch (error) {
      console.error("Error resending invite:", error);
      notifyError("Failed to resend invite. Please try again.");
    }
  };

  // New delete handler
  const handleDeletePendingTeacher = async (teacher) => {
    // Confirmation dialog
    const confirmDelete = window.confirm(
      `Are you sure you want to delete the pending invitation for ${teacher.name}? This action cannot be undone.`
    );
    
    if (!confirmDelete) return;

    try {
      await deletePendingTeacher(teacher.id);
      
      // Remove the teacher from the local state
      setCombinedTeachers((prevTeachers) =>
        prevTeachers.filter((t) => t.id !== teacher.id)
      );
      
      notifySuccess(`Pending invitation for ${teacher.name} has been deleted successfully.`);
    } catch (error) {
      console.error("Error deleting pending teacher:", error);
      notifyError("Failed to delete pending teacher. Please try again.");
    }
  };

  const activeTeachersWithStatus = props?.teachers?.map((teacher) => ({
    ...teacher,
    status: "Active",
  }));

  useEffect(() => {
    if (!schoolid) return;

    const loadPendingTeachers = async (schoolid) => {
      try {
        const pendingTeachers = await getPendingTeacherBySchoolId(schoolid);

        const pendingTeachersWithStatus = pendingTeachers
          .map((teacher) => ({
            ...teacher,
            status: "Pending",
          }))
          .sort((a, b) => a.name.localeCompare(b.name));

        const sortedActiveTeachers = activeTeachersWithStatus.sort((a, b) =>
          a.name.localeCompare(b.name)
        );

        const combinedList = [
          ...pendingTeachersWithStatus,
          ...sortedActiveTeachers,
        ];

        setCombinedTeachers(combinedList);
      } catch (error) {
        console.error("Failed to load pending teachers:", error);
        const sortedActiveTeachersFallback = activeTeachersWithStatus.sort(
          (a, b) => a.name.localeCompare(b.name)
        );
        setCombinedTeachers(sortedActiveTeachersFallback);
      }
      setLoading(false);
    };

    loadPendingTeachers(schoolid);
  }, [schoolid, props.teachers]);

  useEffect(() => {
    const alpacaLead = props?.teachers?.find((teacher) =>
      teacher.additionalRoles?.includes("Alpaca Lead")
    );
    setCurrentAlpacaLead(alpacaLead);
  }, [props?.teachers]);

  const handleAlpacaLeadChange = (newAlpacaLeadId) => {
    const newAlpacaLead = props?.teachers.find(
      (teacher) => teacher.id === newAlpacaLeadId
    );
    setCurrentAlpacaLead(newAlpacaLead);
  };

  function formatDate(dateString) {
    if (!dateString) return "";
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return "";
    return format(date, "MMMM d, yyyy h:mm aa");
  }

  return (
    <div className="colleagues-overview-container">
      <ToastContainer
        position="top-center"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={true}
        closeOnClick
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />
      <Container className="container-shape">
        <Row>
          <Col className="col-4">
            <Row className="mt-4">
              <Col className="col-5">
                <SimpleNavigatioButton
                  path="/masterdashboard"
                  label="Back"
                  colour="blue"
                />
              </Col>
              <h2>{props.schoolName}</h2>
            </Row>
          </Col>
        </Row>
        <hr />
        <Row>
          {loading ? (
            <p>Loading...</p>
          ) : (
            <Table bordered hover className="my-colleagues-table">
              <thead>
                <tr>
                  <th className="name-col">Name</th>
                  <th className="role-col">Roles</th>
                  <th className="status-col">Status</th>
                  <th className="email-col">Email</th>
                  <th className="action-col">Actions</th>
                </tr>
              </thead>
              <tbody>
                {combinedTeachers?.map((teacher, index) => (
                  <tr
                    key={index}
                    className={
                      teacher.status === "Active"
                        ? "active-teacher"
                        : "pending-teacher"
                    }
                  >
                    <td className="name-col">{teacher?.name}</td>
                    <td className="role-col">
                      {teacher?.roles || teacher.additionalRoles?.join(", ")}
                    </td>
                    <td className="status-col">
                      {teacher?.status} <br />
                      {formatDate(teacher?.lastinvitesent)}
                    </td>
                    <td className="email-col">{teacher?.email || "N/A"}</td>
                    <td className="action-col">
                      {teacher.status === "Pending" && (
                        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                          <Tippy
                            content={
                              canResendInvite(teacher.lastinvitesent)
                                ? "Click to resend invite"
                                : "Wait 10 minutes between sending invites"
                            }
                          >
                            <span style={{ display: "inline-block" }}>
                              <Button
                                variant="success"
                                size="sm"
                                disabled={
                                  !canResendInvite(teacher.lastinvitesent)
                                }
                                onClick={() => handleResendInvite(teacher)}
                              >
                                Resend
                              </Button>
                            </span>
                          </Tippy>
                          
                          <Tippy content="Delete pending invitation">
                            <span style={{ display: "inline-block" }}>
                              <Button
                                variant="danger"
                                size="sm"
                                onClick={() => handleDeletePendingTeacher(teacher)}
                              >
                                Delete
                              </Button>
                            </span>
                          </Tippy>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Row>
        {feedback && (
          <div style={{ color: isError ? "red" : "green", marginTop: "20px" }}>
            {feedback}
          </div>
        )}
      </Container>
    </div>
  );
}

export default UserExistenceOverview;